import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { BookOpen, LogOut, BookPlus, User, Menu, X, Heart, Star, Users, MessageCircle, Home, BookMarked, Tag, ShieldCheck, Camera, BarChart3 } from "lucide-react";
import { ReadsLogo } from "./ReadsLogo";
import { t, useLang } from "../lib/i18n";

const MENU_ITEMS = [
  { to: "/", key: "nav.home", icon: Home, public: true },
  { to: "/discover", key: "nav.discover", icon: BookOpen, public: true },
  { to: "/library", key: "nav.library", icon: BookMarked, auth: true },
  { to: "/add", key: "nav.add_book_full", icon: BookPlus, auth: true },
  { to: "/scan-photo", key: "nav.scan_photo", icon: Camera, auth: true },
  { to: "/recommendations", key: "nav.need_rec", icon: Star, auth: true, highlight: true },
  { to: "/wishlist", key: "nav.wishlist", icon: Heart, auth: true },
  { to: "/lists", key: "nav.lists", icon: Tag, auth: true },
  { to: "/messages", key: "nav.messages", icon: MessageCircle, auth: true, unreadBadge: true },
  { to: "/stats", key: "nav.stats", icon: BarChart3, auth: true },
  { to: "/sub-accounts", key: "nav.sub_accounts", icon: Users, auth: true },
  { to: "/account", key: "nav.account", icon: User, auth: true },
  { to: "/admin", key: "nav.admin", icon: ShieldCheck, adminOnly: true },
  { to: "/subscribe", key: "nav.plus", icon: BookPlus, public: true, brand: true },
];

// Global cache so the red dot is instantly correct on every mount, and
// bumped from anywhere (e.g. after opening a thread → mark_thread_read).
let _unreadCount = 0;
const _unreadListeners = new Set();
export function setUnreadCount(n) {
  _unreadCount = n;
  _unreadListeners.forEach((fn) => fn(n));
}
export function useUnread() {
  const [n, setN] = useState(_unreadCount);
  useEffect(() => {
    _unreadListeners.add(setN);
    return () => _unreadListeners.delete(setN);
  }, []);
  return n;
}

export function Navbar() {
  useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAuthed = user && typeof user === "object";
  const isAdmin = isAuthed && user.role === "admin";
  const isPlus = isAuthed && user.is_plus;
  const [open, setOpen] = useState(false);
  const unread = useUnread();

  // Poll unread count while signed in. Cheap endpoint — a single count query.
  useEffect(() => {
    if (!isAuthed) return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/messages/unread-count");
        if (alive) setUnreadCount(Number(data?.unread || 0));
      } catch {
        /* silent — network hiccups shouldn't spam toasts */
      }
    };
    tick();
    const id = setInterval(tick, 45000);
    return () => { alive = false; clearInterval(id); };
  }, [isAuthed]);

  return (
    <>
      <header className="glass-header sticky top-0 z-50 border-b border-[rgba(10,10,10,0.10)]">
        <div className="max-w-7xl mx-auto px-4 md:px-10 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setOpen(true)}
              className="relative p-2 -ml-2 hover:bg-black/5 rounded-sm transition-colors"
              aria-label="Open menu"
              data-testid="menu-toggle"
            >
              <Menu className="w-5 h-5 text-[#0A0A0A]" strokeWidth={1.5} />
              {isAuthed && unread > 0 && (
                <span
                  className="absolute top-1 right-1 w-2 h-2 rounded-full bg-[#C1272D] ring-2 ring-[#FAF5E6]"
                  aria-label={`${unread} unread`}
                  data-testid="menu-unread-dot"
                />
              )}
            </button>
            <Link to="/" className="flex items-end" data-testid="logo-link" aria-label="Reads home">
              <ReadsLogo size={30} />
            </Link>
          </div>

          <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
            {isAuthed && (
              <Link to="/recommendations" data-testid="nav-for-you-primary" className="block">
                <Button size="sm" className="rounded-full bg-gradient-to-b from-[#F5D061] to-[#C9993A] hover:from-[#F7DA7A] hover:to-[#B58530] text-[#3A2A0A] font-mono uppercase tracking-widest text-[10px] sm:text-xs px-3 sm:px-4 h-9 ring-2 ring-[#8A6A1E]/40 ring-offset-2 ring-offset-[#F2EAD3] shadow-[0_2px_8px_rgba(138,106,30,0.35)] animate-[pulse_3.5s_ease-in-out_infinite]" data-testid="nav-for-you-button">
                  <Star className="w-3.5 h-3.5 sm:mr-1.5" strokeWidth={1.5} />
                  <span className="hidden sm:inline">{t("nav.need_rec")}</span>
                </Button>
              </Link>
            )}
            <Link to="/subscribe" data-testid="nav-subscribe" className={isPlus ? "hidden" : ""}>
              <Button size="sm" className="rounded-full bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-[10px] sm:text-xs px-3 sm:px-4 h-9" data-testid="nav-subscribe-button">
                <BookPlus className="w-3.5 h-3.5 sm:mr-1.5" strokeWidth={1.5} />
                <span className="hidden sm:inline">{t("nav.plus")}</span>
              </Button>
            </Link>
            {isAuthed ? (
              <Link to="/account" data-testid="account-link">
                <Button variant="ghost" size="sm" className="rounded-sm font-mono uppercase tracking-widest text-xs" data-testid="navbar-username">
                  <User className="w-3.5 h-3.5 sm:mr-1.5" strokeWidth={1.5} />
                  <span className="hidden sm:inline">{user.name}</span>
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login" className="hidden sm:block">
                  <Button variant="ghost" size="sm" className="rounded-sm font-mono uppercase tracking-widest text-xs" data-testid="login-link">{t("nav.login")}</Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="rounded-sm bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-[10px] sm:text-xs px-3 sm:px-4" data-testid="register-link">{t("nav.join")}</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Slide-in drawer menu */}
      {open && (
        <div className="fixed inset-0 z-[60]" data-testid="menu-drawer">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <aside className="absolute top-0 left-0 h-full w-80 max-w-[85vw] bg-[#FAF5E6] border-r border-[rgba(10,10,10,0.12)] shadow-2xl flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-[rgba(10,10,10,0.08)]">
              <ReadsLogo size={32} />
              <button onClick={() => setOpen(false)} className="p-2 hover:bg-black/5 rounded-sm transition-colors" data-testid="menu-close">
                <X className="w-5 h-5 text-[#0A0A0A]" strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto p-4 space-y-1">
              {MENU_ITEMS.filter((m) => {
                if (m.adminOnly) return isAdmin;
                if (m.auth) return isAuthed;
                if (m.brand && isPlus) return false; // Hide Reads+ entry once the user is Plus.
                return m.public;
              }).map((m) => {
                const Icon = m.icon;
                return (
                  <NavLink
                    key={m.to}
                    to={m.to}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-3 rounded-sm transition-colors font-mono uppercase tracking-widest text-xs ${
                        isActive
                          ? "bg-[#0A0A0A] text-[#F2EAD3]"
                          : m.highlight
                            ? "bg-gradient-to-r from-[#F5D061]/30 to-[#C9993A]/20 text-[#3A2A0A] hover:from-[#F5D061]/45 hover:to-[#C9993A]/30 ring-1 ring-[#8A6A1E]/40 font-bold"
                            : m.brand
                              ? "text-[#0A0A0A] hover:bg-black/5 font-bold"
                              : "text-[#0A0A0A] hover:bg-black/5"
                      }`
                    }
                    data-testid={`menu-${m.key.split(".").pop()}`}
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                    <span className="flex-1">{t(m.key)}</span>
                    {m.unreadBadge && unread > 0 && (
                      <span
                        className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#C1272D] text-white text-[10px] font-mono"
                        data-testid="menu-messages-unread-badge"
                      >
                        {unread > 99 ? "99+" : unread}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </nav>
            {isAuthed && (
              <div className="p-4 border-t border-[rgba(10,10,10,0.08)]">
                <button
                  onClick={async () => { await logout(); setOpen(false); navigate("/"); }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-black/5 transition-colors font-mono uppercase tracking-widest text-xs text-[#0A0A0A]"
                  data-testid="menu-logout"
                >
                  <LogOut className="w-4 h-4" strokeWidth={1.5} /> {t("nav.signout")}
                </button>
              </div>
            )}
          </aside>
        </div>
      )}
    </>
  );
}
